import { Alert } from '@designsforhealth/dfh-react-components'
import { CircularProgress } from '@material-ui/core'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'

import type { CountryCodeEnum } from '../../../../../graphql/magento'
import {
  CustomerAddressFragment,
  CustomerAddressInput,
  GetCountriesQuery,
} from '../../../../../graphql/magento'
import { lookupKeyFromAddress } from '../../../../../lib/cart/address'
import useSortedCustomerAddresses from '../../../../../lib/cart/hooks/useSortedCustomerAddresses'
import {
  addressMaxLengthProp,
  addressMaxLengthRule,
  addressNameRule,
} from '../../../../../lib/form/address'
import { countrySelectOptionsFor, regionSelectOptionsFor } from '../../../../../lib/m2/address'
import { RequiredSelectField, RequiredTextField } from '../../../../form/RequiredFields'
import TextField from '../../../../form/TextField'
import * as Styled from './styled'

export interface AddressFormValues {
  firstname: string
  lastname: string
  company: string
  street1: string
  street2: string
  city: string
  region: string
  region_id?: string
  postcode: string
  country_code: string
  telephone: string
  default_shipping: boolean
  default_billing: boolean
}

interface AddressFormProps {
  className?: string
  handleSaveAddress: (props: CustomerAddressInput) => Promise<void>
  address?: CustomerAddressFragment
  countries: GetCountriesQuery['countries']
  error?: string
  saveDisabled: boolean
}

const formatSubmittedAddress = (formValues: AddressFormValues): CustomerAddressInput => {
  const regionId = formValues.region_id ? Number(formValues.region_id) : null
  return {
    city: formValues.city,
    company: formValues.company,
    country_code: formValues.country_code as CountryCodeEnum,
    default_billing: formValues.default_billing,
    default_shipping: formValues.default_shipping,
    firstname: formValues.firstname,
    lastname: formValues.lastname,
    postcode: formValues.postcode,
    region:
      regionId != null && regionId > 0
        ? { region_id: regionId }
        : {
            // M2 ignores any attempt to reset or nullify `region_id` when regions are not allowed
            // for a country
            region: formValues.region?.trim() || '',
          },
    street: [formValues.street1, formValues.street2].filter((streetValue) => streetValue),
    telephone: formValues.telephone,
  }
}

export const AddressForm: React.FC<AddressFormProps> = ({
  className,
  handleSaveAddress,
  address,
  countries,
  saveDisabled,
}) => {
  const methods = useForm<AddressFormValues>()
  const { formState, handleSubmit, register, setValue, watch } = methods
  const [duplicateError, setDuplicateError] = useState(false)
  const countriesList = useMemo(
    () => (countries ? countrySelectOptionsFor(countries) : []),
    [countries],
  )

  const defaultCountryCode = useMemo(
    () =>
      (countriesList && address
        ? countriesList.find(({ value }) => value === address.country_code)
        : { value: 'US' }
      )?.value,
    [address, countriesList],
  )
  const currentCountryCode = watch('country_code', defaultCountryCode)
  const regionOptions = useMemo(
    () =>
      countries && currentCountryCode
        ? regionSelectOptionsFor(countries, currentCountryCode, true)
        : [],
    [countries, currentCountryCode],
  )

  // resolve address region to an option from M2 if possible
  const defaultRegionOption = useMemo(() => {
    if (!address || !address.region || currentCountryCode !== address?.country_code) {
      return undefined
    }
    const addressRegion = address.region
    return regionOptions.find(
      ({ region: { code, id, name } }) =>
        id === addressRegion.region_id ||
        code === addressRegion.region_code ||
        name === addressRegion.region,
    )
  }, [address, currentCountryCode, regionOptions])

  useEffect(() => {
    // update the default value of the hidden `region_id` when the default region option is resolved
    setValue('region_id', `${defaultRegionOption?.region.id ?? ''}`)
  }, [defaultRegionOption, setValue])

  const onRegionChange = useCallback(
    (value: unknown): void => {
      // update `region_id` when selected option changes
      const selectedOption = value ? regionOptions.find((region) => region.value === value) : null

      setValue('region_id', selectedOption?.region.id ?? '', {
        shouldDirty: true,
        shouldValidate: true,
      })
    },
    [regionOptions, setValue],
  )

  const { addresses } = useSortedCustomerAddresses()

  const doesAddressExist = (inputAddress: CustomerAddressInput) => {
    // M2 2.3.x did not save region_code correctly via createCustomerAddress. Some addresses
    // may contain the region name in the `region_code property (e.g. Connecticut instead of CT).
    // In order to check for duplicates, we create lookup keys for both the region and region_code
    // properties.
    const inputKeyRegion = lookupKeyFromAddress(inputAddress, 'region')
    const inputKeyRegionCode = lookupKeyFromAddress(inputAddress, 'region_code')
    const currentId = address?.id

    return addresses
      ? addresses.some((savedAddress) => {
          if (!savedAddress || (currentId && savedAddress.id === currentId)) return false
          const lookupKey = lookupKeyFromAddress(savedAddress, 'region_code')
          return lookupKey === inputKeyRegion || lookupKey === inputKeyRegionCode
        })
      : false
  }

  /**
   * This function is called when defaultValue is provided to the 'country_code' field
   * and when the field's selection is changed. It sets/resets the city, region, and postcode fields.
   */
  const onCountryChange = useCallback(
    (currentCountry: unknown): void => {
      if (currentCountry !== defaultCountryCode) {
        setValue('city', '')
        setValue('region', '')
        setValue('postcode', '')
      } else {
        setValue('city', address?.city)
        setValue(
          'region',
          regionOptions.length > 0 ? defaultRegionOption?.value : address?.region?.region,
        )
        setValue('postcode', address?.postcode)
      }
    },
    [
      address?.city,
      address?.postcode,
      address?.region?.region,
      defaultCountryCode,
      defaultRegionOption?.value,
      regionOptions.length,
      setValue,
    ],
  )

  const onSubmit: SubmitHandler<AddressFormValues> = (formValues: AddressFormValues) => {
    setDuplicateError(false)

    const formattedAddress = formatSubmittedAddress(formValues)

    // Prepare address for duplicate address detection. `doesAddressExist` uses `lookupKeyFromAddress`
    // to build lookup key values from `region.region` and `region.region_code` properties. When
    // selecting an existing region in Magento, `formattedAddress` only includes `region.region_id`.
    // Supplement the address with `region.region` and `region.region_code` for use with duplicate
    // detection.
    const regionId = formValues.region_id ? Number(formValues.region_id) : null
    const regionOptionFromId = regionId
      ? regionOptions.find(({ region: { id } }) => id === regionId)
      : undefined
    const formattedAddressForDupDetection: CustomerAddressInput = {
      ...formattedAddress,
      region:
        formattedAddress.region?.region || !regionOptionFromId
          ? formattedAddress.region
          : {
              region_code: regionOptionFromId.region.code,
            },
    }
    if (doesAddressExist(formattedAddressForDupDetection)) {
      setDuplicateError(true)
      return
    }

    return handleSaveAddress(formattedAddress)
  }

  return (
    <FormProvider {...methods}>
      <Styled.Form className={className} noValidate onSubmit={handleSubmit(onSubmit)}>
        <RequiredTextField
          autoComplete="given-name"
          defaultValue={address?.firstname}
          fsExclude
          label="First Name"
          name="firstname"
          optionalRules={addressNameRule}
        />
        <RequiredTextField
          autoComplete="family-name"
          defaultValue={address?.lastname}
          fsExclude
          label="Last Name"
          name="lastname"
          optionalRules={addressNameRule}
        />
        <TextField label="Company (optional)" name="company" defaultValue={address?.company} />
        <RequiredTextField
          autoComplete="address-line1"
          defaultValue={address?.street?.[0]}
          label="Address"
          name="street1"
          inputProps={addressMaxLengthProp}
          optionalRules={addressMaxLengthRule}
          required
        />
        <TextField
          autoComplete="address-line2"
          defaultValue={address?.street?.[1]}
          label="Apartment, unit, suite, etc. (optional)"
          name="street2"
          inputProps={addressMaxLengthProp}
          rules={addressMaxLengthRule}
        />
        <RequiredTextField autoComplete="address-level2" label="City" name="city" required />
        {regionOptions.length > 0 ? (
          <>
            <RequiredSelectField
              autoComplete="address-level1"
              className="dropdown-basic"
              label="State"
              placeholder="Select"
              name="region"
              onChange={onRegionChange}
              options={regionOptions}
              required
            />
            <input type="hidden" name="region_id" ref={register} />
          </>
        ) : (
          <TextField label="State" name="region" />
        )}
        <RequiredTextField autoComplete="postal-code" label="ZIP code" name="postcode" required />
        <RequiredSelectField
          autoComplete="country"
          className="dropdown-basic"
          defaultValue={defaultCountryCode}
          label="Country"
          placeholder="Select"
          name="country_code"
          onChange={onCountryChange}
          options={countriesList}
          required
        />
        <RequiredTextField
          autoComplete="tel"
          defaultValue={address?.telephone}
          label="Phone number"
          name="telephone"
          required
        />
        <Styled.Checkbox
          label={<Styled.LabelSpan>Set this as my default shipping address</Styled.LabelSpan>}
          aria-label="Set this as my default shipping address"
          id="default_shipping"
          name="default_shipping"
          defaultValue={address?.default_shipping ?? false}
        />
        <Styled.Checkbox
          label={<Styled.LabelSpan>Set this as my default billing address</Styled.LabelSpan>}
          aria-label="Set this as my default billing address"
          id="default_billing"
          name="default_billing"
          defaultValue={address?.default_billing ?? false}
        />
        <Styled.SubmitOrCancel>
          <Styled.SubmitButton type="submit" disabled={formState.isSubmitting || saveDisabled}>
            {formState.isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Save Address'
            )}
          </Styled.SubmitButton>
          <Styled.Link to="/account/addresses">Cancel</Styled.Link>
        </Styled.SubmitOrCancel>
        {duplicateError && (
          <Alert type="attention" onDismiss={() => setDuplicateError(false)}>
            Sorry, this address already exists.
          </Alert>
        )}
      </Styled.Form>
    </FormProvider>
  )
}
